import { useEffect, useMemo, useState } from "react";
import { RiArrowDropDownLine } from "react-icons/ri";
import {
    FALLBACK_VIDEOS,
    getVideoCategories,
    normalizeVideo,
    toPublishedVideos,
} from "../utils/videoData";

export default function Videos() {
    const [videos, setVideos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState("");

    const [query, setQuery] = useState("");
    const [category, setCategory] = useState("All");
    const [sort, setSort] = useState("newest"); // newest | oldest | title

    useEffect(() => {
        let mounted = true;

        async function loadVideos() {
            try {
                const res = await fetch("/.netlify/functions/get-videos?status=published");
                const json = await res.json();

                if (!mounted) return;

                if (!res.ok || !json.ok) {
                    setLoadError(json.error || "Failed to load videos.");
                    setVideos(toPublishedVideos(FALLBACK_VIDEOS));
                    return;
                }

                const published = toPublishedVideos(json.videos || []);
                setVideos(published);
                setLoadError("");
            } catch (err) {
                if (!mounted) return;
                setLoadError("Network error while loading videos.");
                setVideos(toPublishedVideos(FALLBACK_VIDEOS));
            } finally {
                if (mounted) setLoading(false);
            }
        }

        loadVideos();
        return () => {
            mounted = false;
        };
    }, []);

    const categories = useMemo(() => getVideoCategories(videos), [videos]);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        let items = videos.filter((v) => {
            const matchesCat = category === "All" || v.category === category;
            if (!q) return matchesCat;
            const hay =
                (v.title || "").toLowerCase() +
                " " +
                (v.excerpt || "").toLowerCase() +
                " " +
                (v.tags || []).join(" ").toLowerCase();
            return matchesCat && hay.includes(q);
        });

        items.sort((a, b) => {
            if (sort === "title") return a.title.localeCompare(b.title);
            if (sort === "oldest") return new Date(a.date) - new Date(b.date);
            // newest
            return new Date(b.date) - new Date(a.date);
        });

        return items;
    }, [videos, query, category, sort]);

    const [active, setActive] = useState(null); // holds the clicked video object
    const isDefaultBrowse = query.trim() === "" && category === "All" && sort === "newest";
    const featuredVideo = isDefaultBrowse
        ? filtered.find((v) => v.isFeatured) || filtered[0]
        : null;
    const gridVideos = featuredVideo
        ? filtered.filter((v) => v.id !== featuredVideo.id)
        : filtered;
    const resultVideos = isDefaultBrowse ? gridVideos : filtered;
    const resultsTitle = isDefaultBrowse
        ? "More videos"
        : `${filtered.length} ${filtered.length === 1 ? "video" : "videos"} found`;

    return (
        <main className="page page--contained videos-page" aria-labelledby="videos-heading">
            <header className="page-header page-header--flush videos-header">
                <h1 id="videos-heading" className="page-title">Videos</h1>
                <p className="page-subtitle videos-subtitle">
                    Explore workshops, Q&amp;A, and short teachings.
                </p>

                <div className="videos-controls" role="group" aria-label="Filters">
                    <div className="videos-search">
                        <input
                            className="field"
                            type="search"
                            placeholder="Search videos…"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            aria-label="Search videos"
                        />
                    </div>

                    <div className="videos-selects">
                        <label className="visually-hidden" htmlFor="category">
                            Category
                        </label>
                        <div className="select-wrap">
                            <select
                                className="field"
                                id="category"
                                value={category}
                                onChange={(e) => setCategory(e.target.value)}
                            >
                                {categories.map((c) => (
                                    <option key={c} value={c}>
                                        {c}
                                    </option>
                                ))}
                            </select>
                            <RiArrowDropDownLine className="select-chevron" aria-hidden="true" />
                        </div>

                        <label className="visually-hidden" htmlFor="sort">
                            Sort
                        </label>
                        <div className="select-wrap">
                            <select
                                className="field"
                                id="sort"
                                value={sort}
                                onChange={(e) => setSort(e.target.value)}
                            >
                                <option value="newest">Newest</option>
                                <option value="oldest">Oldest</option>
                                <option value="title">Title (A–Z)</option>
                            </select>
                            <RiArrowDropDownLine className="select-chevron" aria-hidden="true" />
                        </div>
                    </div>
                </div>
            </header>

            {loading && <div className="page-status videos-empty">Loading videos…</div>}
            {!loading && loadError && (
                <div className="page-status videos-empty">{loadError} Showing fallback videos.</div>
            )}

            {!loading && filtered.length === 0 ? (
                <div className="page-status videos-empty">No videos match your filters.</div>
            ) : (
                <>
                    {!loading && isDefaultBrowse && featuredVideo && (
                        <section className="videos-featured" aria-label="Featured video">
                            <VideoCard
                                v={featuredVideo}
                                onOpen={() => setActive(featuredVideo)}
                                variant="featured"
                            />
                        </section>
                    )}

                    {!loading && resultVideos.length > 0 && (
                        <section className="videos-results" aria-labelledby="videos-results-title">
                            <div className="videos-results-header">
                                <h2 id="videos-results-title">{resultsTitle}</h2>
                            </div>
                            <div
                                className="cards-grid videos-grid"
                                data-count={resultVideos.length}
                            >
                                {resultVideos.map((v) => (
                                    <VideoCard key={v.id} v={v} onOpen={() => setActive(v)} />
                                ))}
                            </div>
                        </section>
                    )}
                </>
            )}

            <Lightbox video={active} onClose={() => setActive(null)} />
        </main>
    );
}

function VideoCard({ v, onOpen, variant = "default" }) {
    const isFeatured = variant === "featured";
    const publishedAt = v.date ? new Date(v.date) : null;
    const formattedDate =
        publishedAt && !Number.isNaN(publishedAt.getTime())
            ? publishedAt.toLocaleDateString(undefined, {
                year: "numeric",
                month: "short",
                day: "numeric",
            })
            : "";

    return (
        <article className={`card video-card ${isFeatured ? "video-card--featured" : ""}`}>
            <button className="thumb" onClick={onOpen} aria-label={`Play ${v.title}`}>
                <img src={v.thumb} alt="" loading="lazy" />
                {v.duration ? <span className="duration">{v.duration}</span> : null}
                <span className="play" aria-hidden="true">
                    <span>▶</span>
                </span>
                <span className="thumb-gradient" aria-hidden="true" />
            </button>

            <div className="meta">
                {isFeatured && <span className="featured-label">Featured</span>}
                <h2 className="title">{v.title}</h2>
                <div className="meta-row">
                    <span className="badge">{v.category}</span>
                    {formattedDate ? <span className="date">{formattedDate}</span> : null}
                </div>
                {v.tags?.length ? (
                    <ul className="tags" aria-label="Tags">
                        {v.tags.map((t) => (
                            <li key={t} className="tag">#{t}</li>
                        ))}
                    </ul>
                ) : null}
            </div>
        </article>
    );
}

function Lightbox({ video, onClose }) {
    if (!video) return null;
    const normalizedVideo = normalizeVideo(video);

    const closeOnBackdrop = (e) => {
        if (e.currentTarget === e.target) onClose();
    };

    return (
        <div className="lightbox" role="dialog" aria-modal="true" onClick={closeOnBackdrop}>
            <button className="lightbox-close" onClick={onClose} aria-label="Close video">
                ✕
            </button>

            <div className="player-wrap">
                {normalizedVideo.youtubeId ? (
                    <iframe
                        className="player"
                        src={`https://www.youtube-nocookie.com/embed/${normalizedVideo.youtubeId}?autoplay=1&rel=0`}
                        title={normalizedVideo.title}
                        allow="autoplay; encrypted-media; picture-in-picture"
                        allowFullScreen
                    />
                ) : (
                    <video className="player" controls autoPlay>
                        <source src={normalizedVideo.src} type="video/mp4" />
                        Your browser does not support HTML5 video.
                    </video>
                )}
            </div>
        </div>
    );
}
